/* global $ */
import { Box, Fade, Modal, styled } from "@mui/material";
import { displayNotification } from "helpers";
import { func, object, oneOf, oneOfType, string, bool, number, instanceOf } from "prop-types";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { FaTimesCircle } from "react-icons/fa";

const StyledBox = styled("div")({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    outline: "none",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto", // Ensures vertical scrolling
    scrollbarColor: "#00000 !important",
});


const StylesCloseIcon = styled("a")({
    color: "#ccc",
    "&:hover": {
        color: "#000",
    },
});

const defaultProps = {
    triggers: () => { },
    footTriggers: null,
    onOpen: () => { },
    onClose: () => { },
    children: null,
    title: "Popup Title Here",
    size: "md",
    canFocusOutClose: false,
    isNested: false,
    defaultShow: false,
    parentContainerModal: document.body,
};

const PopupModal = forwardRef((rawProps, ref) => {

    const props = { ...defaultProps, ...rawProps };

    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => {
        return {
            isOpen,
            openModal,
            closeModal,
        };
    });

    useEffect(() => {
        setIsOpen(props.defaultShow);
    }, [props.defaultShow]);

    const openModal = () => {
        props.onOpen && props.onOpen();
        setIsOpen(true);
    };

    const closeModal = (e, reason) => {
        e && e.preventDefault();
        if (reason && props.canFocusOutClose === false) return false;
        props.onClose && props.onClose();
        setIsOpen(false);
    };


    return (
        <>
            {props.triggers({openModal, closeModal})}
            <Modal
                open={isOpen}
                onClose={closeModal}
                disableEscapeKeyDown={props.canFocusOutClose === false}
                disableScrollLock={false}
                disableEnforceFocus
                disableAutoFocus
                closeAfterTransition
                sx={{
                    zIndex: (props?.customZIndex ? props.customZIndex : 999999999) + (props.isNested ? 20 : 0),
                    // overflow: "hidden",
                    '& .mui-fixed': {
                        overflow: 'visible'
                    },
                    
                }}
                container={props.parentContainerModal}
            >
                <Fade in={isOpen}>
                    <StyledBox
                        sx={{
                            width:
                                props.size === "sm"
                                    ? 300
                                    : props.size === "lg"
                                        ? 800
                                        : props.size === "xl"
                                            ? '65vw'
                                            : props.size === "xxl"
                                                ? 'min(65vw, 100%)'
                                                : 500,
                                                maxWidth: '65vw', 
                                                bgcolor: "background.paper",
                                                boxShadow: 24,
                                                overflowX: 'auto', 
                        }}
                        className="mui-fixed"
                    >
                        {props.title && (
                            
                            <Box className="d-flex " sx={{ px: 2, py: 1, borderBottom: "1px solid #ccc" }}>
                                <h2 className="modal-title" >{isOpen && props.title}</h2>
                                <StylesCloseIcon href="/" onClick={closeModal} className="ms-auto">
                                    <FaTimesCircle size={"15px"} />
                                </StylesCloseIcon>
                            </Box>
                        )}
                        <Box
                            sx={{
                                px: 2,
                                py: 1,
                                overflow: "auto",
                                flexGrow: 1,
                                scrollbarColor: "var(--bg-grey) #FAF9F6 !important",
                            }}
                        >
                            {isOpen && props.children}
                        </Box>
                        {props.footTriggers && (
                            <Box sx={{ px: 2, py: 1, borderTop: "1px solid #ccc" }}>
                                <Box className="d-flex justify-content-end">
                                    {isOpen && props.footTriggers({openModal, closeModal})}
                                </Box>
                            </Box>
                        )}
                    </StyledBox>
                </Fade>
            </Modal>
        </>
    );
});

PopupModal.propTypes = {
    triggers: func,
    footTriggers: oneOfType([func, oneOf([null, ""])]),
    onOpen: func,
    onClose: func,
    children: oneOfType([string, object, func, number, oneOf([null, ""])]),
    title: string.isRequired,
    size: oneOf(["sm", "md", "lg", "xl", "xxl"]),
    canFocusOutClose: bool,
    isNested: bool,
    defaultShow: bool,
    parentContainerModal: oneOfType([func, instanceOf(Element)]),
};

PopupModal.displayName = "PopupModal";

export default PopupModal;
